import { Tooltip } from 'antd';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts/highstock';
import React, { FC, useContext, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { ProjectFinancialsContractType } from 'api/project';
import { Loading } from 'components/atoms/loading';
import { ProjectFinancialsContext } from 'contexts/project-financials';
import { useProjectFinancialsBarCharts } from '../../hooks/bar-charts';
import { ProjectFinancialsComponentType } from '../../types';
import { getFilteredZoomLevels } from '../../utils/common';
import {
  ChartContainer,
  DataCardTitleWrapper,
  HiddenInfo,
  ProjectFinancialsZoomLevelSwitcher,
  RowWrapper,
} from '../common';
import { FinancialsDataCard } from '../data-card';
import { ProjectFinancialsCardEmptyState } from '../empty-state';

export const ProjectFinancialsBarCharts: FC = () => {
  const context = useContext(ProjectFinancialsContext);
  const {
    projectFinancials: {
      startDate,
      endDate,
      projectFinancialDetails: { financialContractType },
    },
    allowedZoomLevels,
  } = context;

  const {
    budgetBarChartOptions,
    budgetBarChartTooltipContent,
    hoursBarChartOptions,
    budgetBarChartZoomLevel,
    hoursBarChartZoomLevel,
    budgetBarChartDataLoading,
    hoursBarChartDataLoading,
    onBudgetBarChartZoomLevelChange,
    onHoursBarChartZoomLevelChange,
  } = useProjectFinancialsBarCharts();

  const filteredBudgetZoomLevels = useMemo(
    () =>
      getFilteredZoomLevels(
        allowedZoomLevels[ProjectFinancialsComponentType.BudgetBar],
        startDate,
        endDate,
      ),
    [allowedZoomLevels, endDate, startDate],
  );

  const filteredHoursZoomLevels = useMemo(
    () =>
      getFilteredZoomLevels(
        allowedZoomLevels[ProjectFinancialsComponentType.HoursBar],
        startDate,
        endDate,
      ),
    [allowedZoomLevels, endDate, startDate],
  );

  return (
    <RowWrapper
      reverse={
        financialContractType === ProjectFinancialsContractType.NONBILLABLE
      }
    >
      <FinancialsDataCard
        primary={
          <DataCardTitleWrapper>
            <FormattedMessage
              id={`project.financials.${financialContractType}.bar_chart.budget.title`}
            />
          </DataCardTitleWrapper>
        }
        content={
          budgetBarChartDataLoading ? (
            <Loading size="large" />
          ) : budgetBarChartOptions == null ? (
            <ProjectFinancialsCardEmptyState />
          ) : (
            <ChartContainer>
              <HighchartsReact
                highcharts={Highcharts}
                options={budgetBarChartOptions}
              />
            </ChartContainer>
          )
        }
        central={
          budgetBarChartOptions != null ? (
            <ProjectFinancialsZoomLevelSwitcher
              tabs={filteredBudgetZoomLevels}
              activeTab={budgetBarChartZoomLevel}
              onTabSwitch={onBudgetBarChartZoomLevelChange}
            />
          ) : (
            <></>
          )
        }
        secondary={
          budgetBarChartTooltipContent ? (
            <Tooltip
              title={budgetBarChartTooltipContent}
              overlayStyle={{ maxWidth: 'unset' }}
              placement="rightTop"
            >
              <HiddenInfo>
                <FormattedMessage
                  id={`project.financials.${financialContractType}.legend.info`}
                />
              </HiddenInfo>
            </Tooltip>
          ) : (
            <></>
          )
        }
      />
      <FinancialsDataCard
        primary={
          <DataCardTitleWrapper>
            <FormattedMessage
              id={`project.financials.${financialContractType}.bar_chart.hours.title`}
            />
          </DataCardTitleWrapper>
        }
        content={
          hoursBarChartDataLoading ? (
            <Loading size="large" />
          ) : hoursBarChartOptions == null ? (
            <ProjectFinancialsCardEmptyState />
          ) : (
            <ChartContainer>
              <HighchartsReact
                highcharts={Highcharts}
                options={hoursBarChartOptions}
              />
            </ChartContainer>
          )
        }
        central={
          <ProjectFinancialsZoomLevelSwitcher
            tabs={filteredHoursZoomLevels}
            activeTab={hoursBarChartZoomLevel}
            onTabSwitch={onHoursBarChartZoomLevelChange}
          />
        }
        secondary={<></>}
      />
    </RowWrapper>
  );
};
